import { objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import * as PropTypes from 'prop-types';
import * as React from 'react';
import { KeyBoardKeys } from '../../utils/keyCodes.js';
import { InputLabel } from '../InputLabel/InputLabel.js';
import { Icon } from '../Icon/Icon.js';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage.js';
import { HelpText } from '../HelpText/HelpText.js';

var _excluded = ["className", "enableClearText", "children", "errormessage", "forwardRef", "helptext", "id", "isOptional", "isValid", "label", "maxLength", "name", "onBlur", "onChange", "onFocus", "onClearText", "required", "tooltipText", "type", "value"];

/* eslint-disable @typescript-eslint/no-explicit-any */

var TextFieldComponent = props => {
  // Extract known attributes from props into variables
  var {
      className,
      // Capture the classname so it cannot be applied to the textfield
      enableClearText,
      children,
      errormessage,
      forwardRef,
      helptext,
      id,
      isOptional,
      isValid,
      label,
      maxLength,
      name,
      onBlur,
      onChange,
      onFocus,
      onClearText,
      required,
      tooltipText,
      type,
      value
      // eslint-disable-line
    } = props,
    attributes = _objectWithoutProperties(props, _excluded);
  var hcid = "".concat(TextFieldComponent.name.toLocaleLowerCase(), "-").concat("10.6.2");
  var modRef = React.useRef(null);
  attributes.id = id;
  attributes.className = 'form-control';
  attributes.name = name || id;
  var labelProps = {
    id,
    isOptional,
    label,
    tooltipText
  };
  if (!attributes.placeholder && type === 'inline-date') {
    attributes.placeholder = 'DD/MM/YYYY';
    attributes.type = 'text';
  } else {
    attributes.type = type;
  }

  // Add maximum length
  if (typeof maxLength === 'number' && maxLength > 0) {
    attributes.maxLength = maxLength;
  }
  if (typeof onBlur === 'function') {
    attributes.onBlur = onBlur;
  }
  if (typeof onChange === 'function') {
    attributes.value = typeof maxLength === 'undefined' ? value : String(value).slice(0, maxLength);
    attributes.onChange = e => {
      // @ts-ignore
      onChange(attributes.name, e.target.value, e);
    };
  } else {
    attributes.defaultValue = typeof maxLength === 'undefined' ? value : String(value).slice(0, maxLength);
  }
  if (typeof onFocus === 'function') {
    attributes.onFocus = onFocus;
  }
  if (typeof required === 'boolean' && required === true) {
    attributes['aria-required'] = true;
  }
  var ErrorMessageComponent; // eslint-disable-line
  var HelpTextComponent; // eslint-disable-line

  if (typeof isValid === 'boolean' && isValid === false) {
    // Invalid
    attributes.className += ' is-invalid';
    attributes['aria-invalid'] = true;

    // Invalid with error message
    if (errormessage) {
      attributes['aria-describedby'] = "".concat(id, "-errormessage");
      ErrorMessageComponent = /*#__PURE__*/React.createElement(ErrorMessage, {
        id: id,
        message: errormessage
      });
    }
  } else if (helptext) {
    attributes['aria-describedby'] = "".concat(id, "-helptext");
    HelpTextComponent = /*#__PURE__*/React.createElement(HelpText, {
      id: id
    }, helptext);
  }
  var onClearClick = () => {
    if (onChange) {
      onChange(attributes.name, '');
    } else {
      var _modRef$current;
      var inputElement = modRef === null || modRef === void 0 ? void 0 : (_modRef$current = modRef.current) === null || _modRef$current === void 0 ? void 0 : _modRef$current.children[0];
      inputElement.defaultValue = '';
      inputElement.value = '';
    }
    onClearText === null || onClearText === void 0 ? void 0 : onClearText();
  };
  var onClearEnter = e => {
    if ([KeyBoardKeys.ENTER, KeyBoardKeys.SPACE].includes(e.key)) {
      var _modRef$current2;
      onClearClick();
      (modRef === null || modRef === void 0 ? void 0 : (_modRef$current2 = modRef.current) === null || _modRef$current2 === void 0 ? void 0 : _modRef$current2.children[0]).focus();
    }
  };
  var onClearMouseDown = e => {
    e.preventDefault();
  };
  var iconAttributes = {
    className: 'btn-action-formfield',
    onClick: onClearClick,
    onMouseDown: onClearMouseDown,
    onKeyPress: onClearEnter,
    screenreader: 'clear text',
    symbol: 'controlClose24'
  };

  /* eslint-disable react/jsx-no-useless-fragment */
  var renderInputButton = () => /*#__PURE__*/React.createElement("div", {
    className: "form-field__with-btn",
    "data-hcid": hcid,
    ref: modRef
  }, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("input", _extends({}, attributes, {
    ref: forwardRef
  })), enableClearText && /*#__PURE__*/React.createElement(Icon, _extends({}, iconAttributes, {
    type: "css"
  })), HelpTextComponent, ErrorMessageComponent));
  return /*#__PURE__*/React.createElement("div", {
    className: "form-group",
    "data-hcid": hcid
  }, /*#__PURE__*/React.createElement(InputLabel, labelProps), renderInputButton());
};
TextFieldComponent.defaultProps = {
  isValid: true,
  type: 'text'
};
TextFieldComponent.displayName = 'TextField';
TextFieldComponent.propTypes = {
  enableClearText: PropTypes.bool,
  errormessage: PropTypes.string,
  helptext: PropTypes.any,
  id: PropTypes.string.isRequired,
  isOptional: PropTypes.bool,
  isValid: PropTypes.bool,
  label: PropTypes.string.isRequired,
  maxLength: PropTypes.number,
  /* Name will default to ID if not provided. ID is required for linking label with input */
  name: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onClearText: PropTypes.func,
  onFocus: PropTypes.func,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  tooltipText: PropTypes.string,
  type: PropTypes.string,
  value: PropTypes.string.isRequired
};
var TextField = /*#__PURE__*/React.forwardRef((props, ref) => /*#__PURE__*/React.createElement(TextFieldComponent, _extends({}, props, {
  forwardRef: ref
})));
TextField.displayName = 'TextField';

export { TextField, TextFieldComponent };
